import React, { useState, useEffect, useCallback } from 'react';
import { useStore } from '../../context/StoreContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../../components/ui/select';
import {
  CreditCard, CircleNotch, CheckCircle, WarningCircle, Eye, EyeSlash, FloppyDisk, Bank, ShieldCheck
} from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PaymentMethodsPage = () => {
  const { currentStore } = useStore();
  const [settings, setSettings] = useState({
    enable_stripe: false,
    stripe_public_key: '',
    stripe_secret_key: '',
    enable_paypal: false,
    paypal_mode: 'sandbox',
    paypal_client_id: '',
    paypal_secret: '',
    paypal_pay_later_enabled: true,
    paypal_pay_later_min: 600,
    enable_cod: false,
    enable_bank_transfer: false,
    bank_iban: '',
    bank_bic: '',
    bank_account_holder: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showStripeSecret, setShowStripeSecret] = useState(false);
  const [showPaypalSecret, setShowPaypalSecret] = useState(false);

  const fetchSettings = useCallback(async () => {
    if (!currentStore) return;
    try {
      const r = await axios.get(`${API_URL}/api/stores/${currentStore.id}`, { withCredentials: true });
      const s = r.data?.settings || {};
      setSettings(prev => ({ ...prev, ...s }));
    } catch { toast.error('Erreur de chargement'); }
    finally { setLoading(false); }
  }, [currentStore]);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const handleSave = async () => {
    if (!currentStore) return;
    if (settings.enable_stripe && (!settings.stripe_public_key || !settings.stripe_secret_key)) {
      toast.error('Renseignez les clés Stripe publique et secrète'); return;
    }
    if (settings.enable_paypal && (!settings.paypal_client_id || !settings.paypal_secret)) {
      toast.error('Renseignez le Client ID et Secret PayPal'); return;
    }
    if (settings.enable_bank_transfer && !settings.bank_iban) {
      toast.error("L'IBAN est obligatoire pour le virement bancaire"); return;
    }
    setSaving(true);
    try {
      // We must send a full StoreSettings payload — fetch existing first to preserve other fields
      const current = await axios.get(`${API_URL}/api/stores/${currentStore.id}`, { withCredentials: true });
      const merged = { ...(current.data?.settings || {}), ...settings };
      await axios.put(`${API_URL}/api/stores/${currentStore.id}/settings`, merged, { withCredentials: true });
      toast.success('Méthodes de paiement enregistrées');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally { setSaving(false); }
  };

  const update = (k, v) => setSettings(s => ({ ...s, [k]: v }));

  // Mode detection from key prefixes
  const stripeMode = settings.stripe_secret_key?.startsWith('sk_live_') ? 'live'
                  : settings.stripe_secret_key?.startsWith('sk_test_') ? 'test'
                  : null;

  if (!currentStore) return <div className="text-center py-12 text-slate-500">Sélectionnez une boutique</div>;
  if (loading) return <div className="text-center py-12"><CircleNotch className="animate-spin mx-auto text-slate-400" size={28} /></div>;

  const StatusBadge = ({ ok, label, mode }) => (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider ${ok ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
      {ok ? <CheckCircle size={12} weight="fill" /> : <WarningCircle size={12} weight="fill" />}
      {ok ? (mode ? `${label} · ${mode}` : label) : 'Non configuré'}
    </span>
  );

  return (
    <div className="space-y-6 max-w-4xl" data-testid="payment-methods-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-3">
          <CreditCard size={26} className="text-[#0052FF]" /> Méthodes de paiement
        </h1>
        <p className="text-slate-500 mt-1">Connectez vos comptes Stripe, PayPal et autres modes de paiement acceptés par votre boutique.</p>
      </div>

      {/* ── STRIPE ── */}
      <Card className="border-l-4 border-l-[#635BFF]">
        <CardContent className="p-6 space-y-5">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#635BFF' }}>
                <CreditCard size={26} className="text-white" weight="fill" />
              </div>
              <div>
                <div className="flex items-center gap-2.5">
                  <h3 className="text-lg font-bold text-slate-900">Stripe</h3>
                  <StatusBadge
                    ok={settings.enable_stripe && settings.stripe_secret_key && settings.stripe_public_key}
                    label="Actif"
                    mode={stripeMode}
                  />
                </div>
                <p className="text-sm text-slate-500 mt-1">Carte bancaire, Apple Pay, Google Pay (recommandé)</p>
              </div>
            </div>
            <Switch
              checked={!!settings.enable_stripe}
              onCheckedChange={v => update('enable_stripe', v)}
              data-testid="stripe-enable-switch"
            />
          </div>

          {settings.enable_stripe && (
            <div className="space-y-4 pt-4 border-t">
              <div>
                <Label className="text-sm">Clé publique (publishable key)</Label>
                <Input
                  value={settings.stripe_public_key || ''}
                  onChange={e => update('stripe_public_key', e.target.value)}
                  placeholder="pk_test_... ou pk_live_..."
                  data-testid="stripe-public-key-input"
                  className="font-mono text-xs"
                />
              </div>
              <div>
                <Label className="text-sm">Clé secrète (secret key)</Label>
                <div className="relative">
                  <Input
                    type={showStripeSecret ? 'text' : 'password'}
                    value={settings.stripe_secret_key || ''}
                    onChange={e => update('stripe_secret_key', e.target.value)}
                    placeholder="sk_test_... ou sk_live_..."
                    data-testid="stripe-secret-key-input"
                    className="font-mono text-xs pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowStripeSecret(s => !s)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showStripeSecret ? <EyeSlash size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                <p className="text-[11px] text-slate-400 mt-1.5 flex items-center gap-1">
                  <ShieldCheck size={12} /> Vos clés sont chiffrées et jamais exposées côté client.
                </p>
              </div>
              <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 text-xs text-blue-700">
                <strong>Où trouver vos clés ?</strong> Connectez-vous à <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noopener noreferrer" className="underline font-medium">dashboard.stripe.com/apikeys</a>. Utilisez les clés <code>test</code> pour les tests et <code>live</code> pour la production.
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── PAYPAL ── */}
      <Card className="border-l-4 border-l-[#003087]">
        <CardContent className="p-6 space-y-5">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#003087' }}>
                <span className="text-white font-black text-lg italic">P</span>
              </div>
              <div>
                <div className="flex items-center gap-2.5">
                  <h3 className="text-lg font-bold text-slate-900">PayPal</h3>
                  <StatusBadge
                    ok={settings.enable_paypal && settings.paypal_client_id && settings.paypal_secret}
                    label="Actif"
                    mode={settings.paypal_mode === 'live' ? 'live' : 'sandbox'}
                  />
                </div>
                <p className="text-sm text-slate-500 mt-1">Paiement avec un compte PayPal</p>
              </div>
            </div>
            <Switch
              checked={!!settings.enable_paypal}
              onCheckedChange={v => update('enable_paypal', v)}
              data-testid="paypal-enable-switch"
            />
          </div>

          {settings.enable_paypal && (
            <div className="space-y-4 pt-4 border-t">
              <div>
                <Label className="text-sm">Environnement</Label>
                <Select value={settings.paypal_mode || 'sandbox'} onValueChange={v => update('paypal_mode', v)}>
                  <SelectTrigger data-testid="paypal-mode-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sandbox">Sandbox (tests)</SelectItem>
                    <SelectItem value="live">Production (live)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm">Client ID</Label>
                <Input
                  value={settings.paypal_client_id || ''}
                  onChange={e => update('paypal_client_id', e.target.value)}
                  placeholder="AYSq3RDGsmBL..."
                  data-testid="paypal-client-id-input"
                  className="font-mono text-xs"
                />
              </div>
              <div>
                <Label className="text-sm">Secret</Label>
                <div className="relative">
                  <Input
                    type={showPaypalSecret ? 'text' : 'password'}
                    value={settings.paypal_secret || ''}
                    onChange={e => update('paypal_secret', e.target.value)}
                    placeholder="EGnHDxD_..."
                    data-testid="paypal-secret-input"
                    className="font-mono text-xs pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPaypalSecret(s => !s)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPaypalSecret ? <EyeSlash size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
              <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 text-xs text-blue-700">
                <strong>Où trouver vos identifiants ?</strong> Connectez-vous à <a href="https://developer.paypal.com/dashboard/applications" target="_blank" rel="noopener noreferrer" className="underline font-medium">developer.paypal.com/dashboard</a> → My Apps & Credentials → Choisissez votre app (Sandbox ou Live).
              </div>

              {/* ── Pay Later (4x sans frais) ── */}
              <div className="border-t border-slate-100 pt-4 mt-4">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <Label className="text-sm font-semibold">Paiement 4x sans frais (Pay Later)</Label>
                    <p className="text-xs text-slate-500">Affiche l'option PayPal Pay Later (3x/4x sans frais) au moment du paiement</p>
                  </div>
                  <Switch
                    checked={!!settings.paypal_pay_later_enabled}
                    onCheckedChange={v => update('paypal_pay_later_enabled', v)}
                    data-testid="paypal-pay-later-toggle"
                  />
                </div>
                {settings.paypal_pay_later_enabled && (
                  <div className="space-y-2 bg-slate-50 rounded-lg p-3">
                    <Label className="text-xs">Montant minimum (€)</Label>
                    <Input
                      type="number"
                      step="50"
                      value={settings.paypal_pay_later_min || 600}
                      onChange={e => update('paypal_pay_later_min', parseFloat(e.target.value || 600))}
                      placeholder="600"
                      data-testid="paypal-pay-later-min"
                    />
                    <p className="text-[11px] text-slate-500">
                      Le 4x sans frais ne sera proposé que pour les commandes ≥ ce montant. PayPal exige un minimum de 30 € et un maximum de 2 000 € pour le marché français.
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── BANK TRANSFER ── */}
      <Card className="border-l-4 border-l-slate-700">
        <CardContent className="p-6 space-y-5">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-slate-800 flex items-center justify-center flex-shrink-0">
                <Bank size={26} className="text-white" weight="fill" />
              </div>
              <div>
                <div className="flex items-center gap-2.5">
                  <h3 className="text-lg font-bold text-slate-900">Virement bancaire</h3>
                  <StatusBadge
                    ok={settings.enable_bank_transfer && settings.bank_iban}
                    label="Actif"
                  />
                </div>
                <p className="text-sm text-slate-500 mt-1">Le client paie par virement après confirmation</p>
              </div>
            </div>
            <Switch
              checked={!!settings.enable_bank_transfer}
              onCheckedChange={v => update('enable_bank_transfer', v)}
              data-testid="bank-enable-switch"
            />
          </div>
          {settings.enable_bank_transfer && (
            <div className="space-y-4 pt-4 border-t">
              <div>
                <Label className="text-sm">Titulaire du compte</Label>
                <Input
                  value={settings.bank_account_holder || ''}
                  onChange={e => update('bank_account_holder', e.target.value)}
                  placeholder="MAGERE Viandes"
                  data-testid="bank-holder-input"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm">IBAN</Label>
                  <Input
                    value={settings.bank_iban || ''}
                    onChange={e => update('bank_iban', e.target.value)}
                    placeholder="FR76 ...."
                    data-testid="bank-iban-input"
                    className="font-mono text-xs"
                  />
                </div>
                <div>
                  <Label className="text-sm">BIC / SWIFT</Label>
                  <Input
                    value={settings.bank_bic || ''}
                    onChange={e => update('bank_bic', e.target.value)}
                    placeholder="BNPAFRPP"
                    data-testid="bank-bic-input"
                    className="font-mono text-xs"
                  />
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── COD ── */}
      <Card>
        <CardContent className="p-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-amber-500 flex items-center justify-center flex-shrink-0">
              <span className="text-white font-black text-lg">€</span>
            </div>
            <div>
              <h3 className="text-base font-bold text-slate-900">Paiement à la livraison</h3>
              <p className="text-sm text-slate-500 mt-0.5">Espèces ou chèque remis au livreur</p>
            </div>
          </div>
          <Switch
            checked={!!settings.enable_cod}
            onCheckedChange={v => update('enable_cod', v)}
            data-testid="cod-enable-switch"
          />
        </CardContent>
      </Card>

      {/* ── SAVE ── */}
      <div className="sticky bottom-4 flex justify-end">
        <Button
          onClick={handleSave}
          disabled={saving}
          size="lg"
          className="bg-[#0052FF] hover:bg-[#0040CC] shadow-lg gap-2"
          data-testid="save-payments-btn"
        >
          {saving ? <CircleNotch className="animate-spin" size={18} /> : <FloppyDisk size={18} />}
          Enregistrer les modifications
        </Button>
      </div>
    </div>
  );
};

export default PaymentMethodsPage;
